import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PenLine, Shuffle, Sparkles, Wand2, Zap } from 'lucide-react';
import { interestEmoji } from '../../lib/interests';
import type { IdeaBatch, IdeaLength, IdeaTone, LeadCard } from './types';

/** Cards shown per batch (the endpoint returns four; the fourth feeds the slide's wand). */
export const CARD_COUNT = 3;

const LENGTHS: { length: IdeaLength; Icon: typeof Zap; labelKey: string; tipKey: string }[] = [
  { length: 'short', Icon: Zap, labelKey: 'create.picker.lengthShort', tipKey: 'create.ideasShort' },
  { length: 'medium', Icon: Sparkles, labelKey: 'create.picker.lengthMedium', tipKey: 'create.ideasMedium' },
  { length: 'long', Icon: Wand2, labelKey: 'create.picker.lengthLong', tipKey: 'create.ideasLong' },
];

interface IdeaCardsProps {
  prefix: string;
  batch: IdeaBatch | null;
  loading: boolean;
  length: IdeaLength;
  onLength: (length: IdeaLength) => void;
  onShuffle: () => void;
  /** Current text of the idea box — the matching card renders selected. */
  value: string;
  onPick: (text: string) => void;
  /** Typed text folds the cards away behind a "show ideas" link. */
  collapsed: boolean;
  onShowIdeas: () => void;
  /** Life-event suffix after the title (account). */
  titleSuffix?: ReactNode;
  /** No length ladder / shuffle icon; one "Andere Ideen" button under the cards (trial doors). */
  compact?: boolean;
  /** Dark canvas (the native first run, 2026-10-02): translucent cards and pills, light text. */
  tone?: IdeaTone;
  /** A card ahead of the fetched ideas (first run 3b: the pooled story); two fetched ideas follow it. */
  lead?: LeadCard | null;
  /** An "Eigene Idee" pill next to "Andere Ideen" (compact only) — the composer opens the folded box. */
  onOwnIdea?: () => void;
}

/** Title + length ladder + shuffle, three idea cards (skeletons while loading), fallback note, hint. */
export default function IdeaCards({
  prefix, batch, loading, length, onLength, onShuffle, value, onPick, collapsed, onShowIdeas, titleSuffix, compact = false,
  tone = 'light', lead = null, onOwnIdea,
}: IdeaCardsProps) {
  const { t } = useTranslation();
  const dark = tone === 'dark';
  if (collapsed) {
    return (
      <button type="button" onClick={onShowIdeas} data-testid={`${prefix}-show-ideas`} className="text-sm text-primary hover:underline">
        {t('create.picker.showIdeas')}
      </button>
    );
  }
  const title = batch?.label ? t('create.picker.ideasFor', { theme: batch.label }) : t('create.picker.ideasTonight');
  // The lead card takes the first of the three slots (2026-10-02): three cards in all, like every other surface.
  const ideas = batch ? batch.ideas.slice(0, CARD_COUNT - (lead ? 1 : 0)) : [];
  const cardClass = (selected: boolean) => `w-full text-left p-3 md:p-4 rounded-2xl border-2 text-sm md:text-base leading-snug transition-colors ${
    dark
      ? (selected ? 'border-primary bg-primary/20 text-white' : 'border-white/15 bg-white/[0.06] text-white/90 hover:border-primary/60')
      : (selected ? 'border-primary bg-primary/5' : 'border-stone-200 bg-white hover:border-primary/40')
  }`;
  const pillClass = `inline-flex items-center gap-1.5 min-h-10 px-3.5 rounded-full text-sm transition-colors disabled:opacity-50 ${
    dark ? 'bg-white/10 text-white/85 hover:bg-primary/30 hover:text-white' : 'bg-stone-100 text-foreground hover:bg-primary/10 hover:text-primary'
  }`;
  const mutedText = dark ? 'text-white/55' : 'text-muted-foreground';
  return (
    <div className="space-y-2" data-testid={`${prefix}-cards`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className={`text-sm font-medium ${dark ? 'text-white/85' : 'text-foreground'}`} data-testid={`${prefix}-cards-title`} data-generic={batch?.label ? 'false' : 'true'}>
          {batch?.keyTag && <span aria-hidden="true" className="mr-1">{interestEmoji(batch.keyTag)}</span>}
          {title}
          {titleSuffix}
        </span>
        {!compact && <div className="ml-auto flex items-center gap-2">
          <div className="inline-flex rounded-full bg-stone-100 p-0.5" role="group" aria-label={t('create.inspire')}>
            {LENGTHS.map(({ length: l, Icon, labelKey, tipKey }) => {
              const active = l === length;
              return (
                <button
                  key={l}
                  type="button"
                  onClick={() => onLength(l)}
                  disabled={loading}
                  aria-pressed={active}
                  title={t(tipKey)}
                  data-testid={`${prefix}-length-${l}`}
                  className={`inline-flex items-center gap-1 min-h-9 px-3 py-1.5 rounded-full text-xs transition-colors disabled:opacity-60 ${
                    active ? 'bg-white text-primary font-medium shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t(labelKey)}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={onShuffle}
            disabled={loading}
            data-testid={`${prefix}-shuffle`}
            title={t('create.picker.shuffle')}
            aria-label={t('create.picker.shuffle')}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-stone-100 text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors disabled:opacity-50"
          >
            <Shuffle className={`w-3.5 h-3.5 ${loading ? 'animate-pulse' : ''}`} />
          </button>
        </div>}
      </div>
      {lead && (
        /* Not tied to the fetched list: it shows while the ideas load and survives "Andere Ideen". */
        <button
          type="button"
          onClick={() => onPick(lead.text)}
          aria-pressed={value === lead.text}
          data-testid={`${prefix}-lead-card`}
          className={cardClass(value === lead.text)}
        >
          <span className="font-semibold"><Zap className="inline w-4 h-4 -mt-0.5 mr-1 text-amber-300" aria-hidden="true" />{lead.badge}:</span>{' '}
          {lead.text}
        </button>
      )}
      {loading || !batch ? (
        <div className="space-y-2">
          {[0, 1, 2].slice(0, CARD_COUNT - (lead ? 1 : 0)).map((i) => (
            <div key={i} className={`h-16 rounded-2xl animate-pulse ${dark ? 'bg-white/10' : 'bg-stone-100'}`} />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {ideas.map((idea, i) => {
            const selected = value === idea;
            return (
              <button
                key={`${batch.id}-${batch.length}-${i}`}
                type="button"
                onClick={() => onPick(idea)}
                aria-pressed={selected}
                data-testid={`${prefix}-card-${i}`}
                className={cardClass(selected)}
              >
                {idea}
              </button>
            );
          })}
          {batch.fallback && (
            <p className={`text-[11px] ${mutedText}`} data-testid={`${prefix}-fallback-note`}>{t('create.picker.ideasUnavailable')}</p>
          )}
        </div>
      )}
      <p className={`text-xs ${mutedText}`}>{t(compact ? 'idea.pickHint' : 'create.picker.pickHint')}</p>
      {compact && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onShuffle}
            disabled={loading}
            data-testid={`${prefix}-more-ideas`}
            className={pillClass}
          >
            <Shuffle className={`w-4 h-4 ${loading ? 'animate-pulse' : ''}`} aria-hidden="true" />
            {t('idea.moreIdeas')}
          </button>
          {onOwnIdea && (
            <button type="button" onClick={onOwnIdea} data-testid={`${prefix}-own-idea`} className={pillClass}>
              <PenLine className="w-4 h-4" aria-hidden="true" />
              {t('idea.own')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
